import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { CustomFormField } from "utilities/forms";
import { isValidUrl } from "utilities";

type ValidatableField = Exclude<CustomFormField, { component: "hidden" }>;

const isValidatableField = (field: CustomFormField): field is ValidatableField =>
  field.component !== "hidden";

const isEmptyValue = (value: unknown) =>
  value === undefined || value === null || (typeof value === "string" && value.trim() === "");

export const getLPBuilderContentSettingsValidationSchema = (formFields: CustomFormField[]) =>
  zodResolver(
    z
      .object({})
      .passthrough()
      .superRefine((data, ctx) => {
        formFields.filter(isValidatableField).forEach((field) => {
          if (field.condition && !field.condition(data)) {
            return;
          }

          const value = data[field.name];
          const isRequiredSetting = field.validations?.isRequired;
          const isRequired =
            typeof isRequiredSetting === "function" ? isRequiredSetting(data) : !!isRequiredSetting;

          if (isRequired && isEmptyValue(value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: field.validations?.isRequiredValidationMessage || "This field is required",
              path: [field.name],
            });
            return;
          }

          if (field.validations?.validate) {
            const result = field.validations.validate(value);
            if (result !== true) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: result,
                path: [field.name],
              });
              return;
            }
          }

          if (field.validations?.isValidUrl && !isEmptyValue(value)) {
            if (!isValidUrl(String(value ?? ""))) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  field.validations?.isValidUrlValidationMessage ||
                  "Please enter a valid URL (e.g., https://www.example.com)",
                path: [field.name],
              });
            }
          }
        });
      }),
  );
